import { useEffect, useCallback } from 'react';
import { useAuthStore } from '@/store';
import { useTelegram } from './useTelegram';
import { authenticateTelegram, authenticateDev, api } from '@/services/api';
import type { Character } from '@/types';

export function useAuth() {
  const {
    token,
    user,
    character,
    isAuthenticated,
    isLoading,
    setAuth,
    setCharacter,
    logout,
    setLoading,
  } = useAuthStore();

  const { isReady, isTelegramEnvironment, initData } = useTelegram();

  const authenticate = useCallback(async () => {
    if (!isReady) return;

    setLoading(true);

    try {
      let response;

      if (isTelegramEnvironment && initData) {
        // Auth via Telegram
        response = await authenticateTelegram();
      } else {
        // Dev mode: create test user
        const devTelegramId = Math.floor(Math.random() * 1000000000);
        response = await authenticateDev(devTelegramId, 'Dev User');
      }

      setAuth(response.token, response.user, response.character);
    } catch (error) {
      console.error('Authentication failed:', error);
      logout();
    }
  }, [isReady, isTelegramEnvironment, initData, setAuth, logout, setLoading]);

  // Refresh active character from the server
  const refreshCharacter = useCallback(async () => {
    try {
      const response = await api.get<{ character: Character | null }>('/characters/my');
      if (response.character) {
        setCharacter(response.character);
      }
      return response.character;
    } catch (error) {
      console.error('Failed to refresh character:', error);
      return null;
    }
  }, [setCharacter]);

  // Auto-authenticate on load
  useEffect(() => {
    if (isReady && !isAuthenticated && !token) {
      authenticate();
    } else if (isReady && token) {
      // Token exists, stop loading
      setLoading(false);
    }
  }, [isReady, isAuthenticated, token, authenticate, setLoading]);

  return {
    user,
    character,
    isAuthenticated,
    isLoading,
    authenticate,
    logout,
    setCharacter,
    refreshCharacter,
    hasCharacter: !!character,
  };
}
